/**
 * Brand ordering and search for the marketplace Brand facet.
 *
 * Pure module — no store, no API client, no components. The drawer combobox,
 * the flyout, and the browse-all dialog all rank through here, so surfaces can
 * differ in presentation but never in the order a buyer sees.
 *
 * Ordering contract (spec #24, ratified on #20 and amended after the #21
 * prototype):
 *
 *   empty query : count desc -> FNV-1a("brand-order:" + id) -> raw id
 *   searched    : relevance band -> coverage tier -> count desc -> FNV-1a -> id
 *
 * When counts are withheld (the degraded refetch-failure state) count drops
 * out of both keys, so the client never ranks by numbers it refuses to show:
 *
 *   empty query : FNV-1a -> raw id
 *   searched    : relevance band -> coverage tier -> FNV-1a -> raw id
 *
 * Alphabetical order never participates on any surface: Brand position must
 * not be winnable by renaming ("111aRipple" is the standing anti-goal).
 */

/** Relevance bands, in rank order. Indexes into BAND_LABELS. */
export const BAND_EXACT = 0;
export const BAND_PREFIX = 1;
export const BAND_SUBSTRING = 2;
export const BAND_TYPO = 3;

/** User-visible group labels for the bands above. */
export const BAND_LABELS = ["Exact", "Starts with", "Contains", "Did you mean"];

/**
 * Fuzzy constants (spec #24). These decide match *eligibility* only; they
 * never alter the ordering contract.
 */
export const FUZZY_MIN_QUERY_LENGTH = 4;
export const FUZZY_SHORT_QUERY_LENGTH = 4;
export const FUZZY_MAX_EDITS_SHORT = 1;
export const FUZZY_MAX_EDITS_LONG = 2;

/**
 * Debounce for the Brand search input, in milliseconds. Ranking never reads
 * it; it lives here so every search surface times its input from one ratified
 * constant rather than three copies (spec #24).
 */
export const SEARCH_DEBOUNCE_MS = 200;

/** Coverage cut-offs, high tier first. Deliberately coarse — see coverageTier(). */
export const COVERAGE_TIER_THRESHOLDS = [0.8, 0.4];

/**
 * Normalization for comparison (spec #24): case, punctuation, and repeated
 * whitespace. Everything the module matches on runs through here first.
 */
export function normalizeBrandName(value) {
  return String(value === null || value === undefined ? "" : value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/** FNV-1a, 32-bit. Stable across reloads and identical for every buyer. */
function fnv1a(value) {
  let hash = 0x811c9dc5;
  const str = String(value);
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
}

/**
 * The spec-constant tie order: FNV-1a over `brand-order:${id}`. Client-derived,
 * never persisted, does not rotate, and cannot be moved by renaming. Raw Brand
 * ID is only the hash-collision fallback.
 */
export function brandTieOrder(id) {
  return fnv1a("brand-order:" + id);
}

/**
 * Capped Damerau-Levenshtein (optimal string alignment); bails out as soon as
 * the row can no longer beat `max`. Transposition matters here: "ripel" for
 * "Ripple" is the shape of a real mistyped Brand name, and plain Levenshtein
 * misses it.
 */
function editDistance(a, b, max) {
  if (Math.abs(a.length - b.length) > max) return max + 1;

  let prev2 = null;
  let prev = new Array(b.length + 1);
  let curr = new Array(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;

  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    let best = curr[0];
    for (let j = 1; j <= b.length; j++) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      let value = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
      if (
        prev2 &&
        i > 1 &&
        j > 1 &&
        a.charCodeAt(i - 1) === b.charCodeAt(j - 2) &&
        a.charCodeAt(i - 2) === b.charCodeAt(j - 1)
      ) {
        value = Math.min(value, prev2[j - 2] + 1);
      }
      curr[j] = value;
      if (value < best) best = value;
    }
    if (best > max) return max + 1;
    prev2 = prev;
    prev = curr;
    curr = new Array(b.length + 1);
  }
  return prev[b.length];
}

/**
 * Same-first-character eligibility gate. Buyers rarely mistype the opening
 * letter, and without the gate a two-edit budget drags in pure noise.
 */
function sharesFirstCharacter(candidate, query) {
  return (
    candidate.length > 0 && candidate.charCodeAt(0) === query.charCodeAt(0)
  );
}

/** Edit budget for a query of this length (spec #24). */
function maxEditsFor(queryLength) {
  return queryLength <= FUZZY_SHORT_QUERY_LENGTH
    ? FUZZY_MAX_EDITS_SHORT
    : FUZZY_MAX_EDITS_LONG;
}

/**
 * Coverage tier — how much of the Brand name the match accounts for, bucketed.
 *
 * Ranking by count within a band reproduced the founding failure one level
 * down: "ripel" is exactly two edits from both "Ripple" (12 listings) and
 * "River" of "River Bend Organics" (151), so count buried the Brand the buyer
 * actually typed. Coverage separates them — "ripel" is the whole of "Ripple"
 * but one word of "River Bend Organics".
 *
 * The tiers are deliberately coarse so a trivially shorter name cannot outrank
 * a much larger fragment match: "green" leaves both "Green Tree Collective"
 * and "Green Dot Labs" in the bottom tier, where count still decides. Never
 * used raw.
 *
 * Not gameable by renaming: coverage measures how well a name answers the
 * query, not where the name sits in an alphabet.
 */
function coverageTier(matchedCharacters, nameLength) {
  const coverage = nameLength ? matchedCharacters / nameLength : 0;
  for (let tier = 0; tier < COVERAGE_TIER_THRESHOLDS.length; tier++) {
    if (coverage >= COVERAGE_TIER_THRESHOLDS[tier]) return tier;
  }
  return COVERAGE_TIER_THRESHOLDS.length;
}

/** A typo hit: same opening character, and inside the edit budget. */
function isTypoMatch(candidate, query, max) {
  return (
    sharesFirstCharacter(candidate, query) &&
    editDistance(candidate, query, max) <= max
  );
}

/**
 * Band and matched-character count for one Brand against a normalized query,
 * or null when the Brand does not match at all.
 */
function matchBrand(brand, query) {
  const nameLength = brand.normalized.length;

  if (brand.normalized === query) {
    return { band: BAND_EXACT, matched: nameLength };
  }
  if (
    brand.normalized.startsWith(query) ||
    brand.words.some((word) => word.startsWith(query))
  ) {
    return { band: BAND_PREFIX, matched: query.length };
  }
  if (brand.normalized.indexOf(query) !== -1) {
    return { band: BAND_SUBSTRING, matched: query.length };
  }

  if (query.length < FUZZY_MIN_QUERY_LENGTH) return null;

  const max = maxEditsFor(query.length);
  if (isTypoMatch(brand.normalized, query, max)) {
    return { band: BAND_TYPO, matched: nameLength };
  }
  const word = brand.words.find((candidate) =>
    isTypoMatch(candidate, query, max)
  );
  if (word) return { band: BAND_TYPO, matched: word.length };

  return null;
}

function isPrepared(brand) {
  return !!brand && typeof brand.normalized === "string" && !!brand.words;
}

/**
 * Precompute the comparison keys for one facet option row (`{id, name, count}`
 * from the facet envelope).
 */
function prepareBrand(brand) {
  if (isPrepared(brand)) return brand;

  const normalized = normalizeBrandName(brand.name);
  return Object.freeze(
    Object.assign({}, brand, {
      normalized,
      words: Object.freeze(normalized.split(" ").filter(Boolean)),
      tie: brandTieOrder(brand.id),
    })
  );
}

/**
 * Hoist the comparison keys off the ranking path. A surface holds the prepared
 * rows across keystrokes so a thousand-Brand payload is normalized once per
 * fetch rather than once per search. rankBrands() accepts raw rows too.
 */
export function prepareBrands(brands) {
  return Object.freeze((brands || []).map(prepareBrand));
}

/** A count only participates when it is a real number the client will show. */
function knownCount(brand) {
  return typeof brand.count === "number" && isFinite(brand.count)
    ? brand.count
    : null;
}

/** Raw Brand ID fallback, ordered numerically when the IDs are numeric. */
function compareRawId(a, b) {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a) < String(b) ? -1 : String(a) > String(b) ? 1 : 0;
}

/**
 * Rank the unselected applicable Brands for one query.
 *
 * Selected Brands sit outside this ranking — every surface pins them (drawer
 * chips, dialog rail) and they stay visible at zero count or once unavailable.
 * Unselected zero-count and unavailable Brands are omitted.
 *
 * @param {object}   options
 * @param {object[]} options.brands Facet option rows, raw or prepared.
 * @param {string}   options.query The buyer's raw search text; empty means
 *                   browse rather than search.
 * @param {Array}    options.selectedIds Brand IDs the buyer has selected, or
 *                   drafted in the browse dialog.
 * @param {boolean}  options.countsWithheld True in the degraded state, where
 *                   the client refuses to show counts and so refuses to rank
 *                   by them.
 * @returns {object[]} `{brand, band, bandLabel, tier}` in rank order. Band,
 *                   bandLabel, and tier are null for the empty query, which
 *                   matches nothing and so has no relevance to report.
 */
export function rankBrands({
  brands = [],
  query = "",
  selectedIds = [],
  countsWithheld = false,
} = {}) {
  const normalizedQuery = normalizeBrandName(query);
  const selected = new Set(selectedIds);
  const results = [];

  brands.forEach((row) => {
    const brand = prepareBrand(row);
    if (selected.has(brand.id)) return;
    // A row the buyer has just deselected in a dialog draft can still be a
    // hydration row: only approved, non-archived Brands are available for a
    // new selection, so it does not return to the options.
    if (brand.status === "unavailable") return;

    const count = knownCount(brand);
    if (count !== null && count <= 0) return;

    if (!normalizedQuery) {
      results.push({ brand, band: null, bandLabel: null, tier: null });
      return;
    }

    const hit = matchBrand(brand, normalizedQuery);
    if (!hit) return;

    results.push({
      brand,
      band: hit.band,
      bandLabel: BAND_LABELS[hit.band],
      tier: coverageTier(hit.matched, brand.normalized.length),
    });
  });

  results.sort((a, b) => {
    if (normalizedQuery && a.band !== b.band) return a.band - b.band;
    if (normalizedQuery && a.tier !== b.tier) return a.tier - b.tier;
    if (!countsWithheld) {
      const countA = knownCount(a.brand);
      const countB = knownCount(b.brand);
      // A count only breaks a tie when both are numbers the client will show:
      // an unknown count is unknown, not zero.
      if (countA !== null && countB !== null && countA !== countB) {
        return countB - countA;
      }
    }
    if (a.brand.tie !== b.brand.tie) return a.brand.tie - b.brand.tie;
    return compareRawId(a.brand.id, b.brand.id);
  });

  return results;
}
